import { useState } from 'react';
import { cardHasPrintedPT } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

/**
 * Modal for setting an exact power/toughness override on a battlefield card.
 * Opened via the context menu "Set Power / Toughness" item.
 * Styled to match CounterModal.
 */
export default function PTModal() {
  const contextMenu = usePlaytesterStore((s) => s.contextMenu);
  const battlefield = usePlaytesterStore((s) => s.battlefield);
  const hordeBattlefield = usePlaytesterStore((s) => s.horde?.battlefield ?? []);
  const setPowerToughness = usePlaytesterStore((s) => s.setPowerToughness);
  const closePTModal = usePlaytesterStore((s) => s.closePTModal);

  const [powerInput, setPowerInput] = useState('');
  const [toughnessInput, setToughnessInput] = useState('');

  if (!contextMenu) return null;

  const { instanceId } = contextMenu;

  const instance =
    battlefield.find((i) => i.instanceId === instanceId) ??
    hordeBattlefield.find((i) => i.instanceId === instanceId);

  if (!instance) return null;

  // Initialise inputs from current override or printed value when the modal opens
  const currentPower =
    instance.power !== undefined
      ? String(instance.power)
      : instance.card.power ?? instance.card.cardFaces?.[0]?.power ?? '0';

  const currentToughness =
    instance.toughness !== undefined
      ? String(instance.toughness)
      : instance.card.toughness ?? instance.card.cardFaces?.[0]?.toughness ?? '0';

  const displayPower = powerInput !== '' ? powerInput : currentPower;
  const displayToughness = toughnessInput !== '' ? toughnessInput : currentToughness;

  function handleClose() {
    closePTModal();
    setPowerInput('');
    setToughnessInput('');
  }

  function handleApply() {
    const p = parseInt(displayPower, 10);
    const t = parseInt(displayToughness, 10);
    if (!isNaN(p) && !isNaN(t)) {
      setPowerToughness(instanceId, p, t);
    }
    handleClose();
  }

  return (
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center bg-black/50"
      onClick={handleClose}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[320px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">
            Set P/T —{' '}
            <span className="text-gray-400 font-normal">{instance.card.name}</span>
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-white transition text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4">
          {/* Power / Toughness inputs */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs text-gray-500 font-medium">Power</label>
              <input
                type="number"
                value={displayPower}
                onChange={(e) => setPowerInput(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-full text-center text-sm bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white outline-none focus:border-orange-500 transition"
                autoFocus
              />
            </div>
            <span className="text-gray-500 text-lg font-bold mt-4">/</span>
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs text-gray-500 font-medium">Toughness</label>
              <input
                type="number"
                value={displayToughness}
                onChange={(e) => setToughnessInput(e.target.value)}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                className="w-full text-center text-sm bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-white outline-none focus:border-orange-500 transition"
              />
            </div>
          </div>

          {/* Printed value hint */}
          {cardHasPrintedPT(instance.card) && (
            <p className="text-xs text-gray-600">
              Printed:{' '}
              {(instance.card.power ?? instance.card.cardFaces?.[0]?.power ?? '?')}/
              {(instance.card.toughness ?? instance.card.cardFaces?.[0]?.toughness ?? '?')}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end border-t border-gray-800 pt-3">
            <button
              onClick={handleClose}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded transition"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
